
import { twosComp, convertFastDataToBigint, convertBigintToFastData, convertBigintToUInt32 } from "../NumberHelpers.js";
import { getMemData } from "../../Common/Helpers.js";
import { fromUInt32 } from "../../fable_modules/fable-library-js.5.13.0/BigInt.js";
import { FData } from "../SimTypes.js";
import { printf, toFail } from "../../fable_modules/fable-library-js.5.13.0/String.js";
import { writeAddrBigIntDataBigInt, writeAddrBigIntDataUInt32, writeAddrUInt32DataBigInt, writeAddrUInt32DataUInt32, readAddrBigIntDataBigInt, readAddrBigIntDataUInt32, readAddrUInt32DataBigInt, readAddrUInt32DataUInt32 } from "../RamStore.js";
import { item } from "../../fable_modules/fable-library-js.5.13.0/Array.js";

/**
 * Read the content of the memory at the specified address.
 */
export function readMemoryAddrUInt32DataUInt32(mem, address) {
    return convertBigintToUInt32(mem.WordWidth, getMemData(fromUInt32(address), mem));
}

export function readMemoryAddrUInt32DataBigInt(mem, address) {
    return getMemData(fromUInt32(address), mem);
}

export function readMemoryAddrBigIntDataUInt32(mem, address) {
    return convertBigintToUInt32(mem.WordWidth, getMemData(address, mem));
}

export function readMemoryAddrBigIntDataBigInt(mem, address) {
    return getMemData(address, mem);
}

export function readMemoryFData(mem, address) {
    if (address.tag === 0) {
        return new FData(/* Data */ 0, [convertBigintToFastData(mem.WordWidth, getMemData(convertFastDataToBigint(address.fields[0]), mem))]);
    }
    else {
        return toFail(printf("Can\'t read memory from Algebra"));
    }
}

export function readRamAddrUInt32DataUInt32(ram, address) {
    return readAddrUInt32DataUInt32(ram, address);
}

export function readRamAddrUInt32DataBigInt(ram, address) {
    return readAddrUInt32DataBigInt(ram, address);
}

export function readRamAddrBigIntDataUInt32(ram, address) {
    return readAddrBigIntDataUInt32(ram, address);
}

export function readRamAddrBigIntDataBigInt(ram, address) {
    return readAddrBigIntDataBigInt(ram, address);
}

/**
 * Writes take the step they happen at, because that is what the history is keyed by. They
 * return unit: there is no new memory to thread through, and the old contents the reducer must
 * output are read before the write, as they always were.
 */
export function writeRamAddrUInt32DataUInt32(ram, step, address, data) {
    writeAddrUInt32DataUInt32(ram, step, address, data);
}

export function writeRamAddrUInt32DataBigInt(ram, step, address, data) {
    writeAddrUInt32DataBigInt(ram, step, address, twosComp(ram.WordWidth, data));
}

export function writeRamAddrBigIntDataUInt32(ram, step, address, data) {
    writeAddrBigIntDataUInt32(ram, step, twosComp(ram.AddressWidth, address), data);
}

export function writeRamAddrBigIntDataBigInt(ram, step, address, data) {
    writeAddrBigIntDataBigInt(ram, step, twosComp(ram.AddressWidth, address), twosComp(ram.WordWidth, data));
}

/**
 * Read a RAM through the algebraic evaluator's value type.
 */
export function readRamFData(ram, address) {
    if (address.tag === 0) {
        return new FData(/* Data */ 0, [convertBigintToFastData(ram.WordWidth, readAddrBigIntDataBigInt(ram, convertFastDataToBigint(address.fields[0])))]);
    }
    else {
        return toFail(printf("Can\'t read memory from Algebra"));
    }
}

export function writeRamFData(ram, step, address, data) {
    writeAddrBigIntDataBigInt(ram, step, convertFastDataToBigint(address), convertFastDataToBigint(data));
}

/**
 * The state-array entry a RAM's store lives in.
 * 
 * Every step holds the *same* entry: the store is mutable and shared, so what a slot records is
 * which memory this is, not what it contained. Reducers therefore fetch this value and put the
 * very same object back rather than building `RamState store` again - constructing that union
 * case allocates, and a fresh one per clock is precisely the per-step cost this design exists
 * to remove.
 */
export function getRamState(step, state) {
    if (state == null) {
        return toFail(printf("What? getRamState called on a component with no state array"));
    }
    else {
        const matchValue = item(step, state.Step);
        if (matchValue.tag === 3) {
            return matchValue;
        }
        else {
            return toFail(printf("What? getRamState called before the RAM\'s store was created"));
        }
    }
}

export function ramStoreOf(state) {
    if (state.tag === 3) {
        return state.fields[0];
    }
    else {
        return toFail(printf("What? ramStoreOf called on a component state that is not a RAM\'s"));
    }
}

/**
 * The store a RAM keeps its contents in, for a step that only reads it.
 */
export function getRamStore(step, state) {
    return ramStoreOf(getRamState(step, state));
}

export function getRomStateMemory(comp) {
    const matchValue = comp.FType;
    let matchResult, memory;
    switch (matchValue.tag) {
        case 44: {
            matchResult = 0;
            memory = matchValue.fields[0];
            break;
        }
        case 43: {
            matchResult = 0;
            memory = matchValue.fields[0];
            break;
        }
        default:
            matchResult = 1;
    }
    switch (matchResult) {
        case 0:
            return memory;
        default:
            return toFail(printf("What? getRomStateMemory called with invalid state"));
    }
}

